import { useEffect, useRef } from "react";

/**
 * Drives a native `<dialog>` from a boolean.
 *
 * Worth a hook of its own because the imperative half of `<dialog>` has two
 * sharp edges, and both are silent:
 *
 * - `showModal()` on an already-open dialog throws `InvalidStateError`.
 * - `close()` on an already-closed one fires a `close` event anyway, which a
 *   parent listening for it reads as the user having closed something they
 *   never opened.
 *
 * Both are avoided by checking `dialog.open` first, which is the whole of the
 * hook. What it buys is the browser's own modal behavior: a real focus trap,
 * Escape, the top layer, and a `::backdrop` - each of which is a bug waiting to
 * happen when hand-rolled with a div and a `keydown` listener.
 *
 * @param isOpen - Whether the dialog should be showing.
 * @returns A ref to attach to the `<dialog>` element.
 */
export function useNativeDialog(isOpen: boolean) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) dialog.showModal();
    if (!isOpen && dialog.open) dialog.close();
  }, [isOpen]);

  return ref;
}
